import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { HospitalMasterItem } from '@/types/settings-api';

/**
 * Hospital master search — PROD-761 commit 10
 * (Residencies + Fellowships picker).
 *
 * GET /api/Master/Hospital?searchQuery={q}
 *
 * Source for the Educational → Residencies and → Fellowships HOSPITAL
 * picker (edit_residency_widget.dart, edit_fellowship_widget.dart —
 * same backend, same shape, just different attach points in the parent
 * `EducationWorkExperience` payload).
 *
 * Master entries carry PascalCase `Id` + `HospitalName`. Consumers map:
 *   - `HospitalMasterItem.Id`           → `EducationResidencyOrFellowship.orgnizationId`
 *                                          (typo preserved — backend ships
 *                                          `orgnizationId`, NOT `organizationId`)
 *   - `HospitalMasterItem.HospitalName` → `EducationResidencyOrFellowship.orgnizationName`
 *
 * Wire-shape verified against live dev API 2026-05-14:
 *   [{ "Id": "5ff2216e-…", "HospitalName": "Medcare Hospital" }]
 *
 * Caller debounces 250ms.
 */
export function useHospitalsSearch(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery({
    queryKey: ['settings', 'master', 'hospitals', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<HospitalMasterItem[]>(
        `/api/Master/Hospital?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}
